import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import PrivateRoute from 'components/Navigation/ProtectedRoute';
import BaseInput from 'components/Inputs/BaseInput';
import { showErrorToast } from 'components/Toast';
import SolidButton from 'components/SolidButton';
import Table from 'components/Table';

import { IOperation } from 'utils/interfaces';
import { formatDate } from 'utils/functions';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import {
  ADMIN_OPERATIONS,
  CARDS,
  OPERATIONS_AUX,
  OPERATION_STATUS,
  PAY_MODES,
  PROOF_KINDS,
  REQUIRED_RULE,
  TO_ACCOUNTS,
} from 'utils/constants';
import { ITableColumn } from 'components/Table/interfaces';
import { utils, writeFile } from 'xlsx';

export default function OperationsReportPage() {
  const history = useNavigate();
  const auth = useAuth();
  const {
    register,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      report_kind: 'daily',
    },
  });

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [loading, setLoading] = useState(false);
  const [operations, setOperations] = useState<Array<IOperation>>([]);

  const reportKind = watch('report_kind', 'daily');

  const tableColumns: Array<ITableColumn> = [
    {
      title: 'ESTADO',
      dataKey: 'status',
      render: (item: IOperation) => OPERATION_STATUS[item.status],
    },
    {
      title: 'CÓDIGO',
      dataKey: 'code',
    },
    {
      title: 'FECHA',
      dataKey: 'created_at',
      render: (item: IOperation) =>
        formatDate(new Date(item.created_at), 'yyyy/MM/dd, hh:mm a', false),
    },
    {
      title: 'Clase Ben',
      dataKey: 'to_account',
      render: (item: IOperation) => TO_ACCOUNTS[item.to_account],
    },
    {
      title: 'Cliente',
      key: 'customer',
      dataKey: 'user',
      render: (item: IOperation) =>
        item.user.level !== '30'
          ? item.user.company_name
          : `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`,
    },
    {
      title: 'Usuario',
      dataKey: 'user',
      render: (item: IOperation) =>
        item.user.level !== '30'
          ? `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`
          : '-',
    },
    {
      title: 'Beneficiario',
      dataKey: 'to_name',
      render: (item: IOperation) => (
        <>
          <p>
            {item.user.level !== '30' && item.to_account === 'P' ? 'RUC' : item.to_doc}:{' '}
            {item.user.level !== '30' && item.to_account === 'P'
              ? item.user.username.split('|')[1]
              : item.to_number}
          </p>
          <p>
            {item.user.level !== '30' && item.to_account === 'P'
              ? item.user.company_name
              : item.to_name}
          </p>
        </>
      ),
    },
    {
      title: 'Importe',
      dataKey: 'amount',
    },
    {
      title: 'TIPO DE OPERACIÓN',
      dataKey: 'action',
      render: (item: IOperation) => ADMIN_OPERATIONS[OPERATIONS_AUX[item.action]],
    },
    {
      title: 'Precio',
      dataKey: 'final_rate',
    },
    {
      title: 'Ingresa',
      dataKey: 'origin_bank',
      render: (item: IOperation) =>
        item.amount && item.final_rate ? (
          <>
            <p>
              {item.action === 'C'
                ? (item.amount * item.final_rate)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : item.amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            </p>
            <p>{item.origin_bank}</p>
          </>
        ) : (
          item.origin_bank
        ),
    },
    {
      title: 'Sale',
      dataKey: 'destiny_bank',
      render: (item: IOperation) =>
        item.amount && item.final_rate ? (
          <>
            <p>
              {item.action === 'C'
                ? item.amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : (item.amount * item.final_rate)
                    ?.toFixed(2)
                    ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            </p>
            <p>{item.destiny_bank}</p>
          </>
        ) : (
          item.destiny_bank
        ),
    },
    {
      title: 'Método de pago',
      dataKey: 'pay_mode',
      render: (item: IOperation) => (
        <>
          <p>{PAY_MODES[item.pay_mode]}</p>
          {item.pay_mode === 'RC' ? (
            <>
              <p>{item.to_acc_number}</p>
              {item.to_cci && <p>CCI: {item.to_cci}</p>}
            </>
          ) : item.to_card ? (
            <>
              <p>{CARDS[item.to_card]}</p>
              <p>{item.to_acc_number}</p>
            </>
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      title: 'Prueba',
      dataKey: 'proof',
      render: (item: IOperation) => (
        <>
          {item.proof === 'mail' ? (
            <p>{PROOF_KINDS[item.proof]}</p>
          ) : item.proof === 'file' ? (
            <a
              href={item.proof_URL}
              target="_blank"
              rel="noreferrer"
              className="text-blue-700 underline hover:text-blue-800"
            >
              {PROOF_KINDS[item.proof]}
            </a>
          ) : (
            '-'
          )}
        </>
      ),
    },
  ];

  useEffect(() => {
    const getTodayOperations = async () => {
      setLoading(true);
      const result = await generalApi.get('/daily_report');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setOperations(result.data);
      setLoading(false);
    };
    if (reportKind === 'daily') getTodayOperations();
  }, [generalApi, reportKind]);

  const getRangeOperations = async (data: any) => {
    setLoading(true);
    const result = await generalApi.post('/operation_report', data);
    if (!result.success) {
      showErrorToast(result.message);
      setLoading(false);
      return;
    }
    setOperations(result.data);
    setLoading(false);
  };

  const exportData = async () => {
    if (operations.length === 0) return showErrorToast('No hay operaciones para exportar');
    var createXLSLFormatObj = [];
    var xlsHeader = [
      'Estado',
      'Código',
      'Fecha',
      'Clase',
      'Cliente',
      'Usuario',
      'Beneficiario',
      'Importe',
      'Tipo',
      'Precio',
      'Ingresa',
      'Sale',
      'Método',
      'Prueba',
    ];
    createXLSLFormatObj.push(xlsHeader);
    for (let i = 0; i < operations.length; i++) {
      const item = operations[i];
      const innerRowData = [];
      innerRowData.push(OPERATION_STATUS[item.status]);
      innerRowData.push(item.code);
      innerRowData.push(formatDate(new Date(item.created_at), 'yyyy/MM/dd, hh:mm a', false));
      innerRowData.push(TO_ACCOUNTS[item.to_account]);
      innerRowData.push(
        item.user.level !== '30'
          ? item.user.company_name
          : `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`
      );
      innerRowData.push(
        item.user.level !== '30'
          ? `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`
          : '-'
      );
      innerRowData.push(
        `${item.user.level !== '30' && item.to_account === 'P' ? 'RUC' : item.to_doc}: ${
          item.user.level !== '30' && item.to_account === 'P'
            ? item.user.username.split('|')[1]
            : item.to_number
        } / ${
          item.user.level !== '30' && item.to_account === 'P'
            ? item.user.company_name
            : item.to_name
        }`
      );
      innerRowData.push(item.amount);
      innerRowData.push(ADMIN_OPERATIONS[OPERATIONS_AUX[item.action]]);
      innerRowData.push(item.final_rate);
      innerRowData.push(
        item.amount && item.final_rate
          ? `${
              item.action === 'C'
                ? (item.amount * item.final_rate)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : item.amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            } (${item.origin_bank})`
          : item.origin_bank
      );
      innerRowData.push(
        item.amount && item.final_rate
          ? `${
              item.action === 'C'
                ? item.amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : (item.amount * item.final_rate)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            } (${item.destiny_bank})`
          : item.destiny_bank
      );
      innerRowData.push(
        `${PAY_MODES[item.pay_mode]} / ${
          item.pay_mode === 'RC'
            ? `${item.to_acc_number} ${item.to_cci ? `- CCI: ${item.to_cci}` : ''}`
            : item.to_card
            ? `${CARDS[item.to_card]}: ${item.to_acc_number}`
            : '-'
        }`
      );
      innerRowData.push(item.proof === 'file' ? item.proof_URL : 'Correo');
      createXLSLFormatObj.push(innerRowData);
    }
    const filename = 'Reporte.xlsx';
    const ws_name = 'Clientes';
    const wb = utils.book_new(),
      ws = utils.aoa_to_sheet(createXLSLFormatObj);
    utils.book_append_sheet(wb, ws, ws_name);
    writeFile(wb, filename);
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        watch={watch}
        control={control}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Reporte de Operaciones</p>
          </div>
          <form
            className="mb-6 grid gap-4 bg-white py-6 px-4 shadow-md sm:grid-cols-2 lg:grid-cols-4 2xl:rounded-lg"
            onSubmit={handleSubmit(getRangeOperations)}
          >
            {buildBaseInput('Tipo de reporte', 'select', 'report_kind', REQUIRED_RULE, {
              options: [
                { value: 'daily', name: 'Hoy' },
                { value: 'range', name: 'Rango' },
              ],
            })}
            {reportKind === 'range' && (
              <>
                {buildBaseInput('Desde', 'date', 'from', REQUIRED_RULE, {})}
                {buildBaseInput('Hasta', 'date', 'to', REQUIRED_RULE, {})}
              </>
            )}
            <div className="flex items-center space-x-2 pr-2">
              {reportKind === 'range' && (
                <SolidButton
                  className="w-1/2"
                  color={BUTTON_COLORS.BLUE}
                  onClick={() => handleSubmit(getRangeOperations)}
                >
                  Buscar
                </SolidButton>
              )}
              <SolidButton
                className="w-1/2"
                color={BUTTON_COLORS.BLUE}
                onClick={() => exportData()}
              >
                Exportar
              </SolidButton>
            </div>
          </form>
          <Table
            isLoading={loading}
            loadingText="Buscando operaciones"
            initialSort={{
              key: 'created_at',
              order: 'desc',
            }}
            columns={tableColumns}
            data={operations}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
